// RankFrame — khung xếp hạng quanh ảnh đại diện (CEO 5b · 11b, 17/10/2026; vẽ thật
// theo khung hạng LMHT + kiểu dáng nữ tính 6a·7a, bậc nghề 2a·3a cùng ngày).
//
// Hai bản:
//   • ĐỦ: tranh khung (kiểu dáng người đeo chọn × kim loại của món) + hai viên ngọc màu
//     theo nhóm ở ổ đỉnh/đáy + ánh kim quét. Tranh tràn ra ngoài hộp ảnh tới ~30% cỡ
//     ảnh bằng định vị tuyệt đối — hộp ảnh giữ nguyên kích thước, nơi đặt phải tự chừa
//     lề. Tự bật từ 96px; nhỏ hơn thì chỉ khi nơi gọi đã chừa lề (`ban="du"`).
//     Có bậc nghề ⇒ biển "G<n>" thay viên ngọc dưới, đúng chính giữa phía dưới.
//   • GỌN (mặc định dưới 96px — danh sách, hồ sơ, hộp thoại): vòng kim loại sát mép
//     ảnh + ánh quét, không tràn sang chữ bên cạnh. Vẫn động (11b). Không biển bậc.
// Ảnh luôn đứng thẳng, tròn, chính giữa lỗ khung.
import type { CSSProperties, ReactNode } from "react";
import { frameArtFor, metalOf, plaqueFor, type Metal } from "./frame-art";
import "./rank-frame.css";

/** Đo ảnh thật 17/10/2026: hồ sơ 56px, hộp chi tiết 72px, Dashboard 80px — bản đủ đè tên. */
const DU_TU = 96;
/** Biển bậc nghề rộng bằng ~40% ảnh; dưới mức này chữ G không đọc nổi. */
const BIEN_TU = 56;

type FrameInfo = { code: string; tier: number; group: string };
type CraftTier = { block: string; grade: number };

type Props = {
  frame: FrameInfo | null | undefined;
  style?: string | null | undefined;
  craftTier?: CraftTier | null | undefined;
  sizePx: number;
  className?: string | undefined;
  /** Ép bản: "du" ở nơi đã chừa lề cho khung tràn ra, "gon" ở nơi chật. Bỏ trống = theo cỡ. */
  ban?: "du" | "gon" | undefined;
  children: ReactNode;
};

export function RankFrame({ frame, style, craftTier, sizePx, className, ban, children }: Props) {
  const gon = ban ? ban === "gon" : sizePx < DU_TU;
  const hop: CSSProperties = { width: sizePx, height: sizePx };
  const coBien = !gon && sizePx >= BIEN_TU && !!craftTier && craftTier.grade > 0;

  if (!frame) {
    // Chưa đeo khung mà có bậc nghề: chỉ gắn biển ở ảnh đủ lớn.
    if (!coBien) return <>{children}</>;
    return (
      <span
        className={`rf ${className ?? ""}`}
        style={hop}
        data-khung="bac-nghe"
        data-khung-ban="du"
      >
        {children}
        <Bien metal="bronze" grade={craftTier!.grade} sizePx={sizePx} tamY={sizePx * 0.98} />
      </span>
    );
  }

  const metal = metalOf(frame.tier, frame.group);
  if (gon) {
    return (
      <span
        className={`rf rf--${metal} ${className ?? ""}`}
        style={hop}
        data-khung={frame.code}
        data-khung-ban="gon"
      >
        {children}
        <span className="rf-vong" aria-hidden="true" />
      </span>
    );
  }

  const { art, gem, geo, key } = frameArtFor(frame.tier, frame.group, style);
  const canh = sizePx * geo.layer;
  const lech = (sizePx - canh) / 2;
  const lop = {
    width: canh,
    height: canh,
    left: lech,
    top: lech,
    "--rf-art": `url("${art}")`,
  } as CSSProperties;
  const ngoc = (tamPhanTram: number, caoPhanTram: number, vi: "tren" | "duoi") => (
    <img
      className={`rf-ngoc rf-ngoc--${vi}`}
      data-ngoc={vi}
      src={gem}
      alt=""
      style={{ top: `${tamPhanTram}%`, height: `${caoPhanTram / 0.9}%` }}
    />
  );

  return (
    <span
      className={`rf rf--${metal} ${className ?? ""}`}
      style={hop}
      data-khung={frame.code}
      data-khung-ban="du"
    >
      {children}
      <span className="rf-tranh" style={lop} aria-hidden="true">
        <img className="rf-tranh__anh" src={art} alt="" data-kieu={key} />
        <span className="rf-tranh__quet" />
        {ngoc(geo.gemTopC, geo.gemTopH, "tren")}
        {!coBien && geo.gemBotH > 0 && ngoc(geo.gemBotC, geo.gemBotH, "duoi")}
      </span>
      {coBien && (
        <Bien
          metal={metal}
          grade={craftTier!.grade}
          sizePx={sizePx}
          tamY={lech + (canh * geo.gemBotC) / 100}
        />
      )}
    </span>
  );
}

/** Biển bậc nghề — tấm kim loại vẽ thật, chữ "G<n>" đặt bằng HTML cho luôn nét. */
function Bien({
  metal,
  grade,
  sizePx,
  tamY,
}: {
  metal: Metal;
  grade: number;
  sizePx: number;
  tamY: number;
}) {
  const rong = Math.round(sizePx * 0.4);
  const cao = Math.round(rong / 2.4);
  const st = {
    width: rong,
    height: cao,
    left: (sizePx - rong) / 2,
    top: tamY - cao / 2,
    fontSize: Math.max(10, Math.round(cao * 0.56)),
    backgroundImage: `url("${plaqueFor(metal)}")`,
  } as CSSProperties;
  return (
    <span
      className={`rf-bien rf-bien--${metal}`}
      style={st}
      data-bac-nghe=""
      title={`Bậc nghề G${grade}`}
    >
      G{grade}
    </span>
  );
}
